// MCP URLs over mcp.v1. The server builds every URL on its own public base URL: the browser's
// API host can be an internal name (dev) that an MCP client on the internet cannot reach.
//
// Listing and revoking are plain table access through the SQL firewall — the mcp-urls page and
// the usercredentials model extension.

import GlobalClient from "../hermesclient/client";
import { ContentType, RpcFrameType } from "../hermesclient/proto/wsmessages";
import { nameMappingService } from "./NameMappingService";
import { asLogicalMailbox, MailboxAddress } from "../hermesclient/types";

let continuumMailbox: MailboxAddress | null = null;

/** Calls an mcp.v1 endpoint (served on the "continuum" mailbox); throws on an RPC or errorMessage failure. */
async function mcpRpc<T extends { errorMessage?: string }>(endPoint: string, req: object): Promise<T> {
  if (!continuumMailbox) {
    continuumMailbox = await nameMappingService.resolve(asLogicalMailbox("continuum"));
  }

  const resp = await GlobalClient.get().rawRpcCall({
    to: continuumMailbox,
    endPoint,
    body: new TextEncoder().encode(JSON.stringify(req)),
    contentType: ContentType.Json,
  });

  if (resp.header?.rpcHeader?.frameType === RpcFrameType.ErrorResponse) {
    throw new Error(resp.header?.rpcHeader?.errorInfo?.message ?? `${endPoint} failed`);
  }

  const parsed = JSON.parse(new TextDecoder().decode(resp.data)) as T;
  if (parsed.errorMessage) throw new Error(parsed.errorMessage);
  return parsed;
}

/**
 * A URL's tool set as typed: comma or newline separated tool names, endpoint names or prefixes
 * ending in "*"; empty means every tool.
 */
export function parseToolSet(text: string): string[] {
  return [...new Set(text.split(/[,\n]/).map((t) => t.trim()).filter((t) => t !== ""))];
}

/**
 * One MCP credential's two URLs (FEATURE-20261102-one-mcp-credential-bearer-and-oauth): url, the
 * bearer token URL for Claude, acting as whoever minted it ("" when its token has lapsed or it was
 * minted before every credential had one); oauthUrl, for ChatGPT, where each person signs in.
 */
export type McpUrls = { credentialUid?: string; url: string; oauthUrl: string };

/** Mints a credential for the caller: one tool set, both URLs. */
export async function mintMcpUrl(label: string, specialSauce: string, tools: string[] = []): Promise<McpUrls> {
  const r = await mcpRpc<{ credentialUid?: string; url?: string; oauthUrl?: string; errorMessage?: string }>(
    "mcp.v1.MintUrl",
    { label, specialSauce, tools },
  );
  if (!r.credentialUid || !r.url || !r.oauthUrl) throw new Error("mcp.v1.MintUrl returned no URL");
  return { credentialUid: r.credentialUid, url: r.url, oauthUrl: r.oauthUrl };
}

/** Re-reads both URLs of one of the caller's own live credentials. */
export async function getMcpUrl(credentialUid: string): Promise<McpUrls> {
  const r = await mcpRpc<{ url?: string; oauthUrl?: string; errorMessage?: string }>("mcp.v1.GetUrl", { credentialUid });
  if (!r.oauthUrl) throw new Error("mcp.v1.GetUrl returned no URL");
  return { url: r.url ?? "", oauthUrl: r.oauthUrl };
}

/**
 * Moves the expiry of one of the caller's own credentials forward; the URL does not change.
 * Returns the new expiry (RFC 3339).
 */
export async function extendMcpUrl(credentialUid: string): Promise<string> {
  const r = await mcpRpc<{ expiresAt?: string; errorMessage?: string }>("mcp.v1.ExtendUrl", { credentialUid });
  if (!r.expiresAt) throw new Error("mcp.v1.ExtendUrl returned no expiry");
  return r.expiresAt;
}

/**
 * Replaces the tool set of one of the caller's own credentials; the URL does not change, and the
 * next connection on it gets the new set. Returns the set as stored.
 */
export async function setMcpUrlTools(credentialUid: string, tools: string[]): Promise<string[]> {
  const r = await mcpRpc<{ tools?: string[]; errorMessage?: string }>("mcp.v1.SetUrlTools", { credentialUid, tools });
  return r.tools ?? [];
}

/** A node of the tool tree (mcp.v1.ListTools): a group holding groups and tools. */
export type McpToolInfo = { name: string; description?: string; entry: string; alwaysOffered?: boolean };
export type McpToolGroup = { name: string; label?: string; entry?: string; groups?: McpToolGroup[]; tools?: McpToolInfo[] };

/** Every tool this server can offer, as the chooser's tree: continuum's tools by family, then one group per app. */
export async function listMcpTools(): Promise<McpToolGroup[]> {
  const r = await mcpRpc<{ groups?: McpToolGroup[]; errorMessage?: string }>("mcp.v1.ListTools", {});
  return r.groups ?? [];
}

/** One issued MCP auth (mcp.v1.ListAuths). Never carries the token. */
export type McpAuth = {
  authUid: string;
  kind: "token" | "oauth";
  userUid?: string;
  userName?: string;
  urlCredentialUid?: string;
  urlLabel?: string;
  issuedAt?: string;
  expiresAt?: string;
  tools?: string[];
  toolsOverridden?: boolean;
  urlTools?: string[];
};

/** Every live MCP auth that has been issued. Admin. */
export async function listMcpAuths(): Promise<McpAuth[]> {
  const r = await mcpRpc<{ auths?: McpAuth[]; errorMessage?: string }>("mcp.v1.ListAuths", {});
  return r.auths ?? [];
}

/**
 * Gives one issued auth its own tool set, or with inherit lets it follow its URL's again. The
 * client's next request gets it. Admin. Returns the set it is offered now.
 */
export async function setMcpAuthTools(
  authUid: string,
  tools: string[],
  inherit: boolean,
): Promise<{ tools: string[]; toolsOverridden: boolean }> {
  const r = await mcpRpc<{ tools?: string[]; toolsOverridden?: boolean; errorMessage?: string }>(
    "mcp.v1.SetAuthTools",
    { authUid, tools, inherit },
  );
  return { tools: r.tools ?? [], toolsOverridden: r.toolsOverridden ?? false };
}
